import { LogOut, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TABS, type TabId } from '../settings';

interface SettingsSidebarProps {
  activeTab: TabId;
  onSelect: (id: TabId) => void;
  signedIn: boolean;
  onSignOut: () => void;
}

/**
 * The settings rail (OG `.v16-side`). Tab links with the OG's sliding left-edge
 * active indicator, then (when signed in) a red "Sign out". Sticky on desktop; a
 * horizontal scroller on mobile.
 */
export function SettingsSidebar({
  activeTab,
  onSelect,
  signedIn,
  onSignOut,
}: SettingsSidebarProps) {
  return (
    <aside className="flex gap-0.5 overflow-x-auto rounded-md border border-edge bg-white/[0.02] p-2.5 md:sticky md:top-8 md:flex-col md:self-start md:overflow-visible">
      {TABS.map((t) => (
        <SidebarLink
          key={t.id}
          icon={t.icon}
          label={t.label}
          active={t.id === activeTab}
          onClick={() => onSelect(t.id)}
        />
      ))}

      {signedIn && (
        <>
          <div className="hidden h-1.5 md:block" />
          <SidebarLink icon={LogOut} label="Sign out" danger onClick={onSignOut} />
        </>
      )}
    </aside>
  );
}

interface SidebarLinkProps {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  danger?: boolean;
  onClick: () => void;
}

function SidebarLink({ icon: Icon, label, active, danger, onClick }: SidebarLinkProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group relative flex shrink-0 items-center gap-3 whitespace-nowrap rounded-[10px] py-2.5 pl-3 pr-3 text-left text-[13px] font-semibold transition-all duration-200',
        active
          ? 'text-hi md:pl-[18px]'
          : danger
            ? 'text-danger/80 hover:bg-danger/[0.08] hover:text-danger'
            : 'text-mid hover:bg-white/[0.04] hover:text-hi',
      )}
    >
      {/* Sliding left-edge active bar (OG `.v16-side-link::before`). */}
      <span
        className={cn(
          'absolute left-0 top-1/2 hidden h-3/5 w-[3px] -translate-y-1/2 rounded-r bg-white transition-transform duration-300 ease-cinematic md:block',
          active ? 'scale-y-100 opacity-100' : 'scale-y-0 opacity-0',
        )}
      />
      <Icon
        size={16}
        strokeWidth={2}
        className={cn('shrink-0 transition-opacity', active ? 'opacity-100' : 'opacity-70')}
      />
      <span>{label}</span>
    </button>
  );
}
